/**
 * Shared lookup contracts for LMS configuration APIs.
 *
 * GET /lms/api/lookups
 * GET /lms/api/lookups/{tableName}
 *
 * Usage:
 *   lookupService.getOptions('LookupLoanTypeCategories')
 *   // → { label, value }[] with label = nameEn or nameAr by current UI language
 */

/** Known LMS lookup table names (pass-through path segment). */
export const LOOKUP_TABLES = {
  LoanTypeCategories: 'LookupLoanTypeCategories',
  FinanceFrameworks: 'LookupFinanceFrameworks',
  ConditionTypes: 'LookupConditionTypes',
  ConditionTriggerEvents: 'LookupConditionTriggerEvents',
  ConditionEvidenceTypes: 'LookupConditionEvidenceTypes',
  ConditionVerificationMethods: 'LookupConditionVerificationMethods',
  CovenantMeasurementTypes: 'LookupCovenantMeasurementTypes',
  CovenantOperators: 'LookupCovenantOperators',
  CovenantUnitTypes: 'LookupCovenantUnitTypes',
  CovenantEvidenceTypes: 'LookupCovenantEvidenceTypes',
  CovenantVerificationMethods: 'LookupCovenantVerificationMethods',
  CovenantCalculationProfiles: 'LookupCovenantCalculationProfiles',
  ClauseNegotiabilities: 'LookupClauseNegotiabilities',
  ClauseDeviationRisks: 'LookupClauseDeviationRisks',
  ClauseWordingFormats: 'LookupClauseWordingFormats',
  /** Not published on BE yet — LookupService returns static Draft/Approved/Retired. */
  ClauseVersionStatuses: 'LookupClauseVersionStatuses',
  /**
   * Selectable language list for T&C / Clause Catalogue (`languageCode` string alias).
   * Seeded with English (`en`) and Arabic (`ar`); retiring hides from new records only.
   */
  Languages: 'LookupLanguages',
  /**
   * The LMS country list — the single source for every country name, and the options the
   * Jurisdiction configuration screen picks from (WI 1063). `alias` is the ISO alpha-2 code
   * (`SA`); `nameEn` is the spaced display name (`Saudi Arabia`), which is also what a T&C record
   * stores in `jurisdictionCode`.
   *
   * NOT the allowed-jurisdiction list: which countries are *allowed* comes from
   * `AllowedJurisdictionService`, because that is configuration with its own status and ordering.
   */
  Countries: 'LookupCountries',
  SecurityProviderTypes: 'LookupSecurityProviderTypes',
  SecurityVerificationProfiles: 'LookupSecurityVerificationProfiles',
  CollateralValuationFrequencies: 'LookupCollateralValuationFrequencies',
  DocumentProvisionMethods: 'LookupDocumentProvisionMethods',
  DocumentVerificationMethods: 'LookupDocumentVerificationMethods',
  DocumentSignatureRequirements: 'LookupDocumentSignatureRequirements',
  DocumentOriginalRequirements: 'LookupDocumentOriginalRequirements',
  DocumentRevisionPolicies: 'LookupDocumentRevisionPolicies',
  RuleSetStatuses: 'LookupRuleSetStatuses',
  ApplicabilityLevels: 'LookupApplicabilityLevels',
  ProductStatuses: 'LookupProductStatuses',
  ProductVersionStatuses: 'LookupProductVersionStatuses',
  Currencies: 'LookupCurrencies',
  ApprovalTypes: 'LookupApprovalTypes',
  ApprovalDecisions: 'LookupApprovalDecisions',
  ComponentTypes: 'LookupComponentTypes',
  FieldDataTypes: 'LookupFieldDataTypes',
  FieldControlTypes: 'LookupFieldControlTypes',
  FieldValueSources: 'LookupFieldValueSources',
  FieldValidationRules: 'LookupFieldValidationRules',
  FieldCardinalityCapabilities: 'LookupFieldCardinalityCapabilities',
  FieldValueShapes: 'LookupFieldValueShapes',
  FieldDefaultScopes: 'LookupFieldDefaultScopes',
  FieldTemporalModes: 'LookupFieldTemporalModes',
  FieldPostActivationChanges: 'LookupFieldPostActivationChanges',
  FieldValueHandlers: 'LookupFieldValueHandlers',
  FieldFormatTypes: 'LookupFieldFormatTypes',
  FieldValidationProfiles: 'LookupFieldValidationProfiles',
  /** Facility Product Template construction (ProductComponents API) — Lookup* table names. */
  ConditionOccurrences: 'LookupConditionOccurrences',
  TimingRelationships: 'LookupTimingRelationships',
  TimeUnits: 'LookupTimeUnits',
  ConditionCadences: 'LookupConditionCadences',
  MeasurementScopes: 'LookupMeasurementScopes',
  AssessmentAnchorTypes: 'LookupAssessmentAnchorTypes',
  ThresholdValueTypes: 'LookupThresholdValueTypes',
  SecuritySatisfactionRules: 'LookupSecuritySatisfactionRules',
  RankingRequirements: 'LookupRankingRequirements',
  PerfectionMethods: 'LookupPerfectionMethods',
  CollateralAssetTypes: 'LookupCollateralAssetTypes',
} as const;

export type LookupTableName = (typeof LOOKUP_TABLES)[keyof typeof LOOKUP_TABLES] | (string & {});

/**
 * Raw lookup row as returned by the LMS lookups API.
 * Labels are bilingual; the UI picks nameEn vs nameAr from the active language.
 */
export interface LookupDto {
  id: number;
  alias: string;
  nameEn: string;
  nameAr: string;
  isActive: boolean;
  /** Short code when the API sends one separately from alias (e.g. ISO 4217 `SAR`). */
  code?: string;
  /**
   * Optional capability flags (Covenant unit types, etc.).
   * Not yet returned by `/lookups` — prefer BE extending the endpoint over FE hardcoding.
   */
  requiresValue?: boolean;
  allowsCurrency?: boolean;
  supportsRange?: boolean;
}

/** Ready-to-bind select option for shared InputComponent / native selects. */
export interface LookupSelectOption {
  label: string;
  value: number;
  alias?: string;
  /**
   * Unlocalised English name. Needed where FE logic has to recognise a specific lookup row and
   * the alias does not carry the business wording; `label` follows the UI language and cannot be
   * compared against.
   */
  nameEn?: string;
  /** ISO / short code when available (currencies, etc.). */
  code?: string;
}

/**
 * Select option for a control persisted as the lookup ALIAS instead of the numeric Id
 * (covenant Calculation Profile — the API field is a string).
 */
export interface AliasSelectOption {
  label: string;
  value: string;
}

/**
 * Standard LMS API envelope.
 * Matches: { success, statusCode, message, data, errorCode }
 */
export interface ApiResponse<T> {
  success?: boolean;
  statusCode?: number;
  message?: string;
  data: T;
  errorCode?: string | null;
  succeeded?: boolean;
}
